import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useAuthStore } from '../../store/authStore';

export interface ScenarioScopeToken {
  key: string;
  version: number;
  tenantId: number | undefined;
  adminId: number | undefined;
  role: string | undefined;
  enabled: boolean;
  controller: AbortController;
}

const ALLOWED_ROLES = ['owner', 'admin', 'manager'];

function identityKey(
  tenantId: number | undefined, adminId: number | undefined, role: string | undefined,
  authenticated: boolean, switching: boolean,
): string {
  return `${tenantId ?? ''}:${adminId ?? ''}:${role ?? ''}:${Boolean(authenticated)}:${Boolean(switching)}`;
}

function liveIdentity() {
  const state = useAuthStore.getState();
  return {
    tenantId: state.tenant?.id, adminId: state.admin?.id, role: state.admin?.role,
    authenticated: state.isAuthenticated, switching: state.isSwitching,
  };
}

function liveIdentityKey(): string {
  const identity = liveIdentity();
  return identityKey(identity.tenantId, identity.adminId, identity.role, identity.authenticated, identity.switching);
}

function isEnabled(
  tenantId: number | undefined, adminId: number | undefined, role: string | undefined,
  authenticated: boolean, switching: boolean,
): boolean {
  return Boolean(authenticated && !switching && tenantId && adminId && role && ALLOWED_ROLES.includes(role));
}

function freshScope(
  key: string, tenantId: number | undefined, adminId: number | undefined, role: string | undefined,
  enabled: boolean, version: number,
): ScenarioScopeToken {
  return { key, version, tenantId, adminId, role, enabled, controller: new AbortController() };
}

/**
 * Identity-only read scope for the scenarios admin UI — deliberately NOT
 * useStaffScope (that hook autofetches branches as a side effect; callers
 * here decide what/when to load). useAuthStore.subscribe fires synchronously
 * on every setState — not deferred to a post-render effect — so an identity
 * transition aborts the in-flight scope's shared AbortController immediately,
 * even for an A->B->A sequence that React would otherwise collapse into a
 * single render that only ever shows A again.
 *
 * All scope bookkeeping after the very first render happens in the effect
 * below (setup/cleanup) and in the synchronous store subscription — never in
 * the render body itself, which React may invoke more than once per commit.
 * Cleanup ALWAYS aborts the current scope's controller synchronously (a real
 * unmount must never leave a usable in-flight signal behind). StrictMode's
 * development-only double-invoke (mount -> cleanup -> mount, all
 * synchronous) therefore retires the first mount's generation for real; the
 * matching setup detects that retirement and builds a genuinely fresh,
 * usable generation for the remount, rather than reviving the retired one.
 */
export function useScenarioScope() {
  const tenantId = useAuthStore((s) => s.tenant?.id);
  const role = useAuthStore((s) => s.admin?.role);
  const adminId = useAuthStore((s) => s.admin?.id);
  const authenticated = useAuthStore((s) => s.isAuthenticated);
  const switching = useAuthStore((s) => s.isSwitching);
  const key = identityKey(tenantId, adminId, role, authenticated, switching);
  const enabled = isEnabled(tenantId, adminId, role, authenticated, switching);

  // Initial value only, for the very first render before the effect below
  // has had a chance to run. Consumers always call capture()/isCurrent()
  // from their own effects, which React runs after this hook's effect has
  // already settled — never a stale render-phase snapshot.
  const scope = useRef<ScenarioScopeToken>(freshScope(key, tenantId, adminId, role, enabled, 0));
  const mounted = useRef(false);

  // A same-tick A->B->A identity transition (e.g. two useAuthStore.setState
  // calls batched inside one React update) correctly aborts the old
  // controller and advances scope.current's version via the synchronous
  // subscription below — but React never learns about it: key/enabled end
  // up textually identical to before, so without this counter the useMemo
  // below would keep returning the SAME memoized scope object, and every
  // consumer's useCallback([scope])-based fetch effect would never re-run —
  // the in-flight (now-aborted) request's eventual resolution is correctly
  // ignored by isCurrent(), but nothing ever issues a fresh one again
  // (permanent loading spinner). bumpRevision forces a real re-render whose
  // useMemo recomputes regardless of whether key/enabled textually changed.
  const [revision, bumpRevision] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    mounted.current = true;
    // If this setup follows a cleanup that already aborted scope.current's
    // controller (StrictMode's simulated remount), that generation is
    // permanently retired — build a fresh, usable one now instead of
    // resuming a controller that can never signal non-aborted again.
    if (scope.current.controller.signal.aborted) {
      scope.current = freshScope(
        scope.current.key, scope.current.tenantId, scope.current.adminId, scope.current.role,
        scope.current.enabled, scope.current.version + 1,
      );
    }
    // Synchronous subscribe (not a selector-driven effect): every setState on
    // the auth store is observed immediately, including transitions that
    // happen between renders (a rapid A->B->A).
    const unsubscribe = useAuthStore.subscribe(() => {
      const liveKey = liveIdentityKey();
      if (scope.current.key !== liveKey) {
        const identity = liveIdentity();
        scope.current.controller.abort();
        scope.current = freshScope(
          liveKey, identity.tenantId, identity.adminId, identity.role,
          isEnabled(identity.tenantId, identity.adminId, identity.role, identity.authenticated, identity.switching),
          scope.current.version + 1,
        );
        // Still mounted: schedule the re-render that makes this transition
        // visible to React/useMemo. Never dispatch once unmount has started
        // (unsubscribe() below runs before this listener could fire again,
        // so this guard only matters for the exact instant cleanup is
        // itself in progress).
        if (mounted.current) bumpRevision();
      }
    });
    return () => {
      mounted.current = false;
      unsubscribe();
      scope.current.controller.abort();
    };
  }, []);

  const capture = useCallback((): ScenarioScopeToken => scope.current, []);

  const isCurrent = useCallback((token: ScenarioScopeToken): boolean => mounted.current
    && token.enabled && token.version === scope.current.version && token.key === scope.current.key
    && !token.controller.signal.aborted && liveIdentityKey() === token.key, []);

  const requestConfig = useCallback((token: ScenarioScopeToken) => {
    if (!isCurrent(token) || !token.tenantId || !token.adminId || !token.role) throw new Error('Stale admin context');
    return {
      signal: token.controller.signal,
      headers: {
        'X-Expected-Admin-Tenant': String(token.tenantId),
        'X-Expected-Admin-Actor': String(token.adminId),
        'X-Expected-Admin-Role': token.role,
      },
    };
  }, [isCurrent]);

  // capture/isCurrent/requestConfig are each already stable for the whole
  // component lifetime (their own deps never change). Without this memo the
  // returned object would get a fresh identity on every render regardless —
  // any consumer's useCallback([scope]) (e.g. a fetch effect) would then see
  // a "changed" dependency on every unrelated state update (including the
  // setState inside that very fetch), re-running its effect and re-fetching
  // forever. Memoizing on the actual identity (key/enabled) plus `revision`
  // keeps the object stable across unrelated renders, while still producing
  // a genuinely new identity after a same-tick A->B->A transition — where
  // key/enabled end up textually unchanged, but the underlying scope was
  // still aborted and regenerated and every consumer must refetch.
  return useMemo(() => ({ key, enabled, capture, isCurrent, requestConfig }),
    [key, enabled, capture, isCurrent, requestConfig, revision]);
}
